import React, { useState, useMemo, useEffect } from "react";
import { gifts, config } from "../lib/api";
import { toast } from "sonner";
import { X, Gift, Check, ArrowRight, Mail, MessageSquare } from "lucide-react";

const DURATION_OPTIONS = [
  { months: 1, label: "1 month", price: 3.99 },
  { months: 3, label: "3 months", price: 11.97 },
  { months: 6, label: "6 months", price: 21.99 },
  { months: 12, label: "12 months", price: 39.99, badge: "Best annual value" },
];

const ALL_DELIVERY_OPTIONS = [
  { value: "email", label: "Email", icon: Mail },
  { value: "sms", label: "Text", icon: MessageSquare },
  { value: "both", label: "Both", icon: Gift },
];

// Client-side US phone regex — matches server-side normalize_us_phone in sms_client.py
const US_PHONE_RE = /^(\+?1[\s.\-]?)?\(?([2-9]\d{2})\)?[\s.\-]?([2-9]\d{2})[\s.\-]?(\d{4})$/;

function formatUsPhoneDisplay(raw) {
  const m = US_PHONE_RE.exec((raw || "").trim());
  if (!m) return raw;
  return `(${m[2]}) ${m[3]}-${m[4]}`;
}

export default function GiftModal({ open, onClose }) {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [durationMonths, setDurationMonths] = useState(1);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  // Load feature flags once when the modal first opens. Falls back to
  // email-only if the flag endpoint fails.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    config
      .features()
      .then((f) => {
        if (!cancelled) setSmsEnabled(!!f?.sms_gifts_enabled);
      })
      .catch(() => {
        if (!cancelled) setSmsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // If Twilio isn't live, force delivery back to Email so the paywall never
  // ships a broken SMS-only gift even if a stale state lingers.
  useEffect(() => {
    if (!smsEnabled && deliveryMethod !== "email") {
      setDeliveryMethod("email");
    }
  }, [smsEnabled, deliveryMethod]);

  const DELIVERY_OPTIONS = useMemo(
    () =>
      smsEnabled
        ? ALL_DELIVERY_OPTIONS
        : ALL_DELIVERY_OPTIONS.filter((o) => o.value === "email"),
    [smsEnabled]
  );

  const needsEmail = deliveryMethod === "email" || deliveryMethod === "both";
  const needsPhone = deliveryMethod === "sms" || deliveryMethod === "both";
  const phoneValid = useMemo(() => US_PHONE_RE.test((recipientPhone || "").trim()), [recipientPhone]);

  if (!open) return null;

  const selectedOption = DURATION_OPTIONS.find((o) => o.months === durationMonths);

  const validate = () => {
    if (!recipientName.trim()) return "Please enter their name.";
    if (needsEmail && !recipientEmail.trim()) return "Recipient email is required for email delivery.";
    if (needsPhone && !phoneValid) return "Please enter a valid US mobile number.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const { url } = await gifts.create({
        recipient_email: needsEmail ? recipientEmail.trim() : null,
        recipient_name: recipientName.trim(),
        recipient_phone: needsPhone ? recipientPhone.trim() : null,
        delivery_method: deliveryMethod,
        duration_months: durationMonths,
        is_anonymous: isAnonymous,
        message: message.trim() || null,
        origin_url: window.location.origin,
      });
      if (!url) {
        toast.error("Could not start gift checkout");
        setBusy(false);
        return;
      }
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (_) {}
      const newTab = window.open(url, "_blank", "noopener,noreferrer");
      if (newTab) {
        setBusy(false);
      } else {
        window.location.href = url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start gift checkout");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="gift-modal"
      className="fixed inset-0 z-[100] bg-[#2E2E2E]/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 fade-in-up"
      onClick={onClose}
    >
      <div
        className="bg-[#F9FAF7] rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#F9FAF7]/95 backdrop-blur-sm flex items-center justify-between p-5 border-b border-[#DCE5DB]">
          <div className="flex items-center gap-2">
            <Gift size={18} className="text-[#4F6F52]" />
            <h2 className="font-display text-xl text-[#2E2E2E]">Give LifeGuide</h2>
          </div>
          <button
            data-testid="gift-close"
            onClick={onClose}
            className="text-[#6B6B6B] hover:text-[#2E2E2E] p-2 rounded-full hover:bg-[#EEF3EC] transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <p className="text-sm text-[#6B6B6B] leading-relaxed">
            One-time payment. Their subscription begins the moment they claim it and lasts
            the full duration you choose. The claim link never expires.
          </p>

          <div>
            <label className="block text-sm text-[#6B6B6B] mb-1.5">Their name</label>
            <input
              data-testid="gift-recipient-name"
              type="text"
              required
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
              placeholder="First name"
            />
          </div>

          <div>
            {smsEnabled ? (
              <>
                <label className="block text-sm text-[#6B6B6B] mb-2">How should we deliver it?</label>
                <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Delivery method">
                  {DELIVERY_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const active = deliveryMethod === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        data-testid={`gift-delivery-${opt.value}`}
                        onClick={() => setDeliveryMethod(opt.value)}
                        className={`rounded-2xl border px-3 py-3 text-center transition inline-flex flex-col items-center gap-1 ${
                          active
                            ? "border-[#4F6F52] bg-[#E1ECDD] text-[#4F6F52] shadow-[0_2px_8px_rgba(79,111,82,0.12)]"
                            : "border-[#DCE5DB] bg-white text-[#2E2E2E] hover:border-[#4F6F52]/40"
                        }`}
                      >
                        <Icon size={14} />
                        <span className="text-sm font-medium">{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#6B6B6B] mt-2 leading-relaxed">
                  Text is a one-time gift-delivery message. Standard message and data rates
                  may apply. No marketing texts.
                </p>
              </>
            ) : (
              <div
                data-testid="gift-delivery-email-only"
                className="rounded-2xl border border-[#DCE5DB] bg-white px-4 py-3 flex items-center gap-2.5"
              >
                <Mail size={14} className="text-[#4F6F52] shrink-0" />
                <span className="text-sm text-[#2E2E2E]">
                  Delivered by email — text delivery coming soon.
                </span>
              </div>
            )}
          </div>

          {needsEmail && (
            <div>
              <label className="block text-sm text-[#6B6B6B] mb-1.5">Their email</label>
              <input
                data-testid="gift-recipient-email"
                type="email"
                required={needsEmail}
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                placeholder="them@example.com"
              />
            </div>
          )}

          {needsPhone && (
            <div>
              <label className="block text-sm text-[#6B6B6B] mb-1.5">
                Their mobile number{" "}
                <span className="text-xs text-[#6B6B6B]/70">(US, required for Text)</span>
              </label>
              <input
                data-testid="gift-recipient-phone"
                type="tel"
                inputMode="tel"
                required={needsPhone}
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                onBlur={() => phoneValid && setRecipientPhone(formatUsPhoneDisplay(recipientPhone))}
                className={`w-full bg-white border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 transition ${
                  recipientPhone && !phoneValid
                    ? "border-[#A86B4A] focus:ring-[#A86B4A]/20"
                    : "border-[#DCE5DB] focus:border-[#4F6F52] focus:ring-[#4F6F52]/15"
                }`}
                placeholder="(555) 123-4567"
              />
              {recipientPhone && !phoneValid && (
                <p className="text-xs text-[#A86B4A] mt-1.5" data-testid="gift-phone-error">
                  Please enter a valid US mobile number.
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm text-[#6B6B6B] mb-2">Duration</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {DURATION_OPTIONS.map((opt) => (
                <button
                  key={opt.months}
                  type="button"
                  data-testid={`gift-duration-${opt.months}`}
                  onClick={() => setDurationMonths(opt.months)}
                  className={`relative rounded-2xl border px-3 py-3 text-center transition ${
                    durationMonths === opt.months
                      ? "border-[#4F6F52] bg-[#E1ECDD] text-[#4F6F52] shadow-[0_2px_8px_rgba(79,111,82,0.12)]"
                      : "border-[#DCE5DB] bg-white text-[#2E2E2E] hover:border-[#4F6F52]/40"
                  }`}
                >
                  {opt.badge && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#4F6F52] text-white text-[9px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                      {opt.badge}
                    </div>
                  )}
                  <div className="text-sm font-medium">{opt.label}</div>
                  <div className="text-xs mt-0.5 opacity-80">${opt.price.toFixed(2)}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#6B6B6B] mb-1.5">
              Message (optional)
            </label>
            <textarea
              data-testid="gift-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={400}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none"
              placeholder="Add a short personal note to your gift."
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="gift-anonymous"
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#4F6F52]"
            />
            <span className="text-sm text-[#2E2E2E] leading-relaxed">
              <span className="font-medium">Make this gift anonymous.</span>
              <span className="text-[#6B6B6B]">
                {" "}They'll see: "Someone who cares about you has given you LifeGuide."
              </span>
            </span>
          </label>

          <div className="rounded-2xl bg-[#EEF3EC] px-4 py-3 border border-[#DCE5DB] flex items-center gap-2">
            <Check size={14} className="text-[#4F6F52] shrink-0" />
            <span className="text-sm text-[#2E2E2E]">
              Total: <strong>${selectedOption.price.toFixed(2)}</strong> · one-time payment
            </span>
          </div>

          <button
            data-testid="gift-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-3.5 font-medium transition inline-flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            {busy ? "Opening checkout..." : `Give LifeGuide — $${selectedOption.price.toFixed(2)}`}
            {!busy && <ArrowRight size={14} />}
          </button>

          <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
            Secure payment via Stripe · the claim link never expires
          </p>
        </form>
      </div>
    </div>
  );
}
